import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuthContext } from '@/context/AuthContext';
import ENDPOINTS from '@/api';
import {
  CampaignStatus,
  TasksResponse,
  CompleteTaskResponse,
  CheckinResponse,
  TaskType,
  TaskCategory,
} from '@/pages/NewYearCampaign/types';

/**
 * Hook to fetch New Year Campaign status
 */
export function useNewYearCampaignStatus() {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['newYearCampaign', 'status'],
    queryFn: async (): Promise<CampaignStatus> => {
      const response = await fetch(ENDPOINTS.newYearCampaignStatus, {
        credentials: 'include',
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to fetch campaign status');
      }

      return json.data;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refresh every minute for countdown
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch tasks with optional filters
 */
export function useNewYearTasks(
  period?: 'daily' | 'weekly' | 'campaign',
  category?: TaskCategory
) {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['newYearCampaign', 'tasks', period, category],
    queryFn: async (): Promise<TasksResponse> => {
      const params = new URLSearchParams();
      if (period) params.set('period', period);
      if (category) params.set('category', category);

      const url = params.toString()
        ? `${ENDPOINTS.newYearCampaignTasks}?${params}`
        : ENDPOINTS.newYearCampaignTasks;

      const response = await fetch(url, {
        credentials: 'include',
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to fetch tasks');
      }

      return json.data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to complete a task
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskType,
      tweetUrl,
      originalTweetUrl,
      screenshotUrl,
    }: {
      taskType: TaskType;
      tweetUrl?: string;
      originalTweetUrl?: string;
      screenshotUrl?: string;
    }): Promise<CompleteTaskResponse> => {
      const response = await fetch(
        `${ENDPOINTS.newYearCampaignTasks}/${taskType}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ tweetUrl, originalTweetUrl, screenshotUrl }),
        }
      );

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to complete task');
      }

      return json.data;
    },
    onSuccess: (data) => {
      if (data.pointsAwarded > 0) {
        toast.success(
          `Task completed! +${data.pointsAwarded.toLocaleString()} points`
        );
      } else if (data.capped) {
        toast.info('Task completed, but you have reached your points cap');
      }

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['newYearCampaign'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to complete task');
    },
  });
}

/**
 * Hook to perform daily check-in
 */
export function useCheckin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<CheckinResponse> => {
      const response = await fetch(ENDPOINTS.newYearCampaignCheckin, {
        method: 'POST',
        credentials: 'include',
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to check in');
      }

      return json.data;
    },
    onSuccess: (data) => {
      toast.success(`Checked in! ${data.newStreak} day streak!`);

      if (data.streakBonusAwarded && data.streakBonusAwarded > 0) {
        setTimeout(() => {
          toast.success(
            `Streak bonus: +${data.streakBonusAwarded.toLocaleString()} points!`
          );
        }, 500);
      }

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['newYearCampaign'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to check in');
    },
  });
}

/**
 * Hook to verify a tweet for tasks
 */
export function useVerifyTweet() {
  return useMutation({
    mutationFn: async ({
      tweetUrl,
    }: {
      tweetUrl: string;
    }): Promise<{ valid: boolean; tweetData?: unknown }> => {
      const response = await fetch(ENDPOINTS.newYearCampaignVerifyTweet, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ tweetUrl }),
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Tweet verification failed');
      }

      return json.data;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Tweet verification failed');
    },
  });
}

/**
 * Hook to fetch campaign leaderboard
 */
export function useNewYearLeaderboard(limit = 50, offset = 0) {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['newYearCampaign', 'leaderboard', limit, offset],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      const response = await fetch(
        `${ENDPOINTS.newYearCampaignLeaderboard}?${params}`,
        {
          credentials: 'include',
        }
      );

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to fetch leaderboard');
      }

      return json.data;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
}
